import React, { useState, useEffect, useMemo } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { FiBookOpen } from "react-icons/fi";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import FileUpload from "../../../components/ui/FileUpload";
import { strings } from "../../../strings/strings";
import { generateSlug, cleanTextContent } from "./seoHelpers";

const stripHtmlTags = (html = "") => {
  return html.replace(/<[^>]*>?/gm, "").trim();
};

const BlogModal = ({ isOpen, onClose, onSave, blog }) => {
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category: "",
    keywords: "",
    author: "",
    image: null,
    metaDescription: "",
    content: "",
    status: "Published",
    publishedAt: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "clean"],
      ],
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "list",
      "bullet",
      "link",
    ],
    []
  );

  useEffect(() => {
    if (isOpen) {
      if (blog) {
        const keywordsValue = Array.isArray(blog.keywords)
          ? blog.keywords.join(", ")
          : (blog.keywords || (Array.isArray(blog.tags) ? blog.tags.join(", ") : ""));

        setFormData({
          title: blog.title || "",
          slug: blog.slug || "",
          category: blog.category || "",
          keywords: keywordsValue,
          author: typeof blog.author === "string" ? blog.author : (blog.author?.name || ""),
          image: blog.coverImage || blog.image || null,
          metaDescription: cleanTextContent(
            blog.metaDescription || blog.seo?.metaDescription || blog.excerpt || ""
          ),
          content: blog.content || "",
          status: blog.status || "Published",
          publishedAt: blog.publishedAt
            ? new Date(blog.publishedAt).toISOString().slice(0, 16)
            : "",
        });
      } else {
        setFormData({
          title: "",
          slug: "",
          category: "",
          keywords: "",
          author: "",
          image: null,
          metaDescription: "",
          content: "",
          status: "Published",
          publishedAt: "",
        });
      }
      setErrors({});
      setIsSubmitting(false);
    } else {
      setFormData({
        title: "",
        slug: "",
        category: "",
        keywords: "",
        author: "",
        image: null,
        metaDescription: "",
        content: "",
        status: "Published",
        publishedAt: "",
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, blog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto-generate slug from title if adding a new blog and slug hasn't been manually altered
      if (name === "title" && !blog) {
        updated.slug = generateSlug(value).toLowerCase().replace(/[^a-z0-9-]/g, "");
      }
      if (name === "slug") {
        updated.slug = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
      }
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleContentChange = (contentHtml) => {
    setFormData((prev) => ({ ...prev, content: contentHtml }));
    if (errors.content) {
      setErrors((prev) => ({ ...prev, content: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Blog title is required";
    } else if (formData.title.length > 250) {
      newErrors.title = "Title cannot exceed 250 characters";
    }

    if (!formData.slug.trim()) {
      newErrors.slug = "URL slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug.trim())) {
      newErrors.slug = "Slug must contain only lowercase letters, numbers, and hyphens";
    }

    const textContent = stripHtmlTags(formData.content);
    if (!formData.content || !textContent) {
      newErrors.content = "Blog content is required";
    }

    if (formData.metaDescription && formData.metaDescription.length > 300) {
      newErrors.metaDescription = "Meta description cannot exceed 300 characters";
    }

    if (formData.status === "Scheduled") {
      if (!formData.publishedAt) {
        newErrors.publishedAt = "Scheduled date is required when status is Scheduled";
      } else if (new Date(formData.publishedAt) <= new Date()) {
        newErrors.publishedAt = "Scheduled date must be in the future";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    const payload = {
      _id: blog?._id || blog?.id,
      title: formData.title.trim(),
      slug: formData.slug.trim().toLowerCase(),
      content: formData.content,
      status: formData.status || "Published", // Only status has a default value
    };

    if (formData.category && formData.category.trim()) {
      payload.category = formData.category.trim();
    } else if (blog) {
      payload.category = "";
    }

    if (formData.author && formData.author.trim()) {
      payload.author = formData.author.trim();
    } else if (blog) {
      payload.author = "";
    }

    if (formData.keywords && formData.keywords.trim()) {
      payload.keywords = formData.keywords.trim();
    } else if (blog) {
      payload.keywords = "";
    }

    if (formData.metaDescription && formData.metaDescription.trim()) {
      payload.metaDescription = formData.metaDescription.trim();
    } else if (blog) {
      payload.metaDescription = "";
    }

    if (formData.publishedAt) {
      payload.publishedAt = new Date(formData.publishedAt).toISOString();
    } else if (blog) {
      payload.publishedAt = "";
    }

    if (formData.image) {
      payload.coverImage = formData.image;
      payload.image = formData.image;
    }

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" onClick={onClose} />

      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-gray-100 dark:border-slate-800 animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-gray-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiBookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {blog ? strings.EDIT_BLOG : strings.ADD_BLOG}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {blog ? "Update the details for this blog" : "Enter details to create a new blog"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg"
          >
            <IoCloseSharp className="w-6 h-6" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-5 scrollbar-thin" id="blog-form" noValidate>
          {/* Title */}
          <Input
            id="title"
            name="title"
            label={strings.BLOG_TITLE}
            value={formData.title}
            onChange={handleChange}
            placeholder={strings.BLOG_TITLE_PLACEHOLDER}
            error={errors.title}
            required
          />

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {strings.BLOG_SLUG} <span className="text-red-500">*</span>
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-xs text-gray-400 font-mono">/blog/</span>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder={strings.BLOG_SLUG_PLACEHOLDER}
                className={`w-full pl-14 pr-4 py-2 rounded-lg border ${
                  errors.slug ? "border-red-500" : "border-gray-300 dark:border-slate-600"
                } bg-gray-50 dark:bg-slate-700 text-xs sm:text-sm text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
              />
            </div>
            {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
          </div>

          {/* Category & Author */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              id="category"
              name="category"
              label={strings.BLOG_CATEGORY}
              value={formData.category}
              onChange={handleChange}
              placeholder={strings.BLOG_CATEGORY_PLACEHOLDER}
            />

            <Input
              id="author"
              name="author"
              label={strings.BLOG_AUTHOR}
              value={formData.author}
              onChange={handleChange}
              placeholder={strings.BLOG_AUTHOR_PLACEHOLDER}
            />
          </div>

          {/* Keywords / SEO Tags */}
          <Input
            id="keywords"
            name="keywords"
            label={strings.KEYWORDS}
            value={formData.keywords}
            onChange={handleChange}
            placeholder={strings.KEYWORDS_PLACEHOLDER}
          />

          {/* Status & Publish Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="Published">Published</option>
                <option value="Draft">Draft</option>
                <option value="Scheduled">Scheduled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Publish Date {formData.status === "Scheduled" && <span className="text-red-500">*</span>}
              </label>
              <input
                type="datetime-local"
                name="publishedAt"
                value={formData.publishedAt}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg border ${
                  errors.publishedAt ? "border-red-500" : "border-gray-300 dark:border-slate-600"
                } bg-gray-50 dark:bg-slate-700 text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none`}
              />
              {errors.publishedAt && <p className="mt-1 text-sm text-red-500">{errors.publishedAt}</p>}
            </div>
          </div>

          {/* Blog Image */}
          <div>
            <FileUpload
              label={strings.BLOG_IMAGE}
              name="image"
              value={formData.image}
              onChange={handleChange}
              error={errors.image}
            />
          </div>

          {/* Meta Description (for SEO) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {strings.META_DESCRIPTION}
            </label>
            <textarea
              name="metaDescription"
              rows={2}
              value={formData.metaDescription}
              onChange={handleChange}
              placeholder={strings.META_DESCRIPTION_PLACEHOLDER}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>

          {/* Blog Content (HTML WYSIWYG Editor) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {strings.BLOG_CONTENT} <span className="text-red-500">*</span>
            </label>
            <div className={`rounded-lg overflow-hidden ${errors.content ? "border-2 border-red-500" : ""}`}>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder={strings.BLOG_CONTENT_PLACEHOLDER}
              />
            </div>
            {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 sm:px-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900 shrink-0">
          <Button type="button" variant="secondary" onClick={onClose} className="px-6">
            {strings.CANCEL}
          </Button>
          <Button type="submit" form="blog-form" variant="primary" className="px-6" disabled={isSubmitting}>
            {isSubmitting ? (blog ? "Updating..." : "Adding...") : (blog ? strings.SAVE_CHANGES : strings.ADD_BLOG)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogModal;
